import React, { useState, useEffect, useMemo, useCallback } from 'react';
import KPICard from './KPICard';
import ChartCard from './ChartCard';
import { LoanDisbursedChart, LoanByStatusChart, LeadTrendChart, BorrowerCompletionChart } from './LoanCharts';
import { Rupee, FileText, CheckCircle2, HandCoins, Users, Clock } from './icons';
import { formatIndianCurrency, formatToLakhs } from '../utils/formatters';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';


const ResponsiveGridLayout = WidthProvider(Responsive);

// --- Chart Config Management ---

const defaultChartConfigs = {
  disbursed: 'ComboChart',
  status: 'PieChart',
  trends: 'LineChart',
  completion: 'PieChart',
};

const availableChartTypes = {
  disbursed: ['ComboChart', 'AreaChart'],
  status: ['PieChart', 'BarChart'],
  trends: ['LineChart', 'AreaChart'],
  completion: ['PieChart', 'BarChart'],
};

const getChartConfigsFromLocalStorage = () => {
    try {
        const stored = localStorage.getItem('dashboardChartConfigs');
        if (stored) return JSON.parse(stored);
    } catch (e) {
        console.error("Failed to parse chart configs from localStorage", e);
    }
    return null;
}

const saveChartConfigsToLocalStorage = (configs) => {
    try {
        localStorage.setItem('dashboardChartConfigs', JSON.stringify(configs));
    } catch (e) {
        console.error("Failed to save chart configs to localStorage", e);
    }
}


// --- Layout Management ---

const generateDefaultLayout = (kpis, charts) => {
    const kpiLayouts = kpis.map((kpi, index) => ({
        i: `kpi-${kpi.key}`,
        x: index % 4,
        y: Math.floor(index / 4),
        w: 1,
        h: 1,
    }));

    const kpiRows = Math.ceil(kpis.length / 4);

    const chartLayouts = charts.map((chart, index) => ({
        i: `chart-${chart.key}`,
        x: (index % 2) * 2,
        y: kpiRows + (Math.floor(index / 2) * 2),
        w: 2,
        h: 2,
        minW: 2,
        minH: 2,
    }));

    return [...kpiLayouts, ...chartLayouts];
};

const getLayoutsFromLocalStorage = () => {
    try {
        const storedLayouts = localStorage.getItem('dashboardLayout');
        if (storedLayouts) {
            return JSON.parse(storedLayouts);
        }
    } catch (e) {
        console.error("Failed to parse layouts from localStorage", e);
    }
    return null;
};

const saveLayoutsToLocalStorage = (layouts) => {
    try {
        localStorage.setItem('dashboardLayout', JSON.stringify(layouts));
    } catch (e) {
        console.error("Failed to save layouts to localStorage", e);
    }
};


const Dashboard = ({ data }) => {
  const { kpis, loanDisbursedOverTime, loanByStatus, leadOverTime, borrowerCompletionStatus } = data;

  const [chartConfigs, setChartConfigs] = useState(
      () => getChartConfigsFromLocalStorage() || defaultChartConfigs
  );
  
  const [layouts, setLayouts] = useState({ lg: [] });
  const [isLayoutInitialized, setIsLayoutInitialized] = useState(false);

  useEffect(() => {
    saveChartConfigsToLocalStorage(chartConfigs);
  }, [chartConfigs]);

  const handleChartTypeChange = useCallback((chartKey, newType) => {
      setChartConfigs(prev => ({ ...prev, [chartKey]: newType }));
  }, []);

  const allKpis = useMemo(() => [
    { key: 'leads', title: "Leads", getValue: (k) => k?.leadCount?.toLocaleString('en-IN'), icon: <FileText className="w-8 h-8 text-green-500" />, tooltipText: "The total number of new loan applications started in the selected period." },
    { key: 'borrowers', title: "Borrowers", getValue: (k) => k?.borrowerCount?.toLocaleString('en-IN'), icon: <Users className="w-8 h-8 text-purple-500" />, tooltipText: "The number of unique customers who have become borrowers." },
    { key: 'approvedLoan', title: "Loan", getValue: (k) => k?.approvedLoanCount?.toLocaleString('en-IN'), icon: <CheckCircle2 className="w-8 h-8 text-teal-500" />, tooltipText: "The total count of approved loans created during the period." },
    { key: 'approvalRate', title: "Approval Rate", getValue: (k) => `${k?.approvalRate.toFixed(1)}%`, icon: <CheckCircle2 className="w-8 h-8 text-teal-500" />, tooltipText: "The percentage of leads that were approved for a loan." },
    { key: 'disbursedAmount', title: "Disbursement Amount", getValue: (k) => formatToLakhs(k?.totalLoanValue), icon: <Rupee className="w-8 h-8 text-blue-500" />, tooltipText: "The total disbursed value of all loans in the selected period." },
    { key: 'disbursedCount', title: "Disbursement Count", getValue: (k) => k?.loanCount?.toLocaleString('en-IN'), icon: <HandCoins className="w-8 h-8 text-orange-500" />, tooltipText: "The total count of disbursed loans." },
    { key: 'avgAmount', title: "Avg. Loan Amount", getValue: (k) => formatIndianCurrency(k.avgLoanAmount), icon: <HandCoins className="w-8 h-8 text-yellow-500" />, tooltipText: "The average value of a disbursed loan." },
    { key: 'avgTat', title: "Avg TAT", getValue: (k) => `${k.avgTat.toFixed(1)} Days`, icon: <Clock className="w-8 h-8 text-indigo-500" />, tooltipText: "Average Turnaround Time from lead creation to loan disbursal." },
  ], []);
//   ], [kpis]);

  const chartDefinitions = useMemo(() => [
      { key: 'disbursed', title: 'Loan Disbursed', component: LoanDisbursedChart, data: loanDisbursedOverTime },
      { key: 'status', title: 'Loan by Status', component: LoanByStatusChart, data: loanByStatus },
      { key: 'trends', title: 'Lead Trends', component: LeadTrendChart, data: leadOverTime },
      { key: 'completion', title: 'Borrower Completion Status', component: BorrowerCompletionChart, data: borrowerCompletionStatus }
  ], [loanDisbursedOverTime, loanByStatus, leadOverTime, borrowerCompletionStatus]);

  useEffect(() => {
    const storedLayouts = getLayoutsFromLocalStorage();
    const defaultLayout = { lg: generateDefaultLayout(allKpis, chartDefinitions) };

    let initialLayouts = defaultLayout;

    if (storedLayouts && storedLayouts.lg?.length > 0) {
        const currentKeys = new Set(storedLayouts.lg.map(l => l.i));
        const requiredKeys = new Set([...allKpis.map(k => `kpi-${k.key}`), ...chartDefinitions.map(c => `chart-${c.key}`)]);

        // Integrity check: if the stored layout doesn't match the current dashboard items, reset to default.
        if (currentKeys.size === requiredKeys.size && [...requiredKeys].every(k => currentKeys.has(k))) {
            initialLayouts = storedLayouts;
        } else {
            console.warn("Layout mismatch detected, resetting to default.");
        }
    }
    
    setLayouts(initialLayouts);
    setIsLayoutInitialized(true);
  }, [allKpis, chartDefinitions]);
  
  const onLayoutChange = (_layout, allLayouts) => {
      // Only save layouts after the initial one has been set to prevent overwriting on mount.
      if (isLayoutInitialized && allLayouts['lg'] && allLayouts['lg'].length > 0) {
        saveLayoutsToLocalStorage(allLayouts);
        setLayouts(allLayouts);
      }
  };
  
  // Don't render the grid until the layout is confirmed to be ready.
  if (!isLayoutInitialized) {
    return null; 
  }

  return (
      <ResponsiveGridLayout
          className="layout"
          layouts={layouts}
          onLayoutChange={onLayoutChange}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 4, md: 4, sm: 2, xs: 1, xxs: 1 }}
          rowHeight={120}
          margin={[24, 24]}
          draggableHandle=".drag-handle"
      >
        {allKpis.map((kpi) => (
            <div key={`kpi-${kpi.key}`} tabIndex={0} className="focus:outline-blue-500 focus:outline-dashed focus:outline-2 focus:outline-offset-2 rounded-xl">
              <KPICard
                  title={kpi.title} 
                  value={kpi.getValue(kpis)}
                  icon={kpi.icon} 
                  tooltipText={kpi.tooltipText}
              />
            </div>
        ))}
        {chartDefinitions.map((chart) => {
            const ChartComponent = chart.component;
            const currentChartType = chartConfigs[chart.key] || defaultChartConfigs[chart.key];
            return (
                <div key={`chart-${chart.key}`} tabIndex={0} className="focus:outline-blue-500 focus:outline-dashed focus:outline-2 focus:outline-offset-2 rounded-xl">
                  <ChartCard 
                      title={chart.title}
                      chartKey={chart.key}
                      currentType={currentChartType}
                      availableTypes={availableChartTypes[chart.key]}
                      onTypeChange={handleChartTypeChange}
                  >
                      <ChartComponent data={chart.data} type={currentChartType} />
                  </ChartCard>
                </div>
            );
        })}
      </ResponsiveGridLayout>
  );
};

export default Dashboard;
